/**
 * Thrown when the BIM Search DB responds with `202 Accepted` for a scene request.
 *
 * The search DB returns 202 when the scene's search database is not present on the
 * shared read path and it has kicked off an OpenZFS backfill from the FAS archive
 * (see bim-search-db `SceneBackfillRequestedExceptionFilter`). The 202 body is an
 * acceptance envelope, not object/search data.
 *
 * Because 202 is a success status, `@procore/core-http` does not treat it as an
 * error — so without this mapping the acceptance envelope would be parsed as if it
 * were the expected payload (empty/garbage object metadata, search hits, or resolve
 * arrays). We surface it as a typed error instead, so callers can show a "preparing"
 * state and retry until the backfill completes.
 */
export class SceneBackfillingError extends Error {
    constructor() {
        super("BIM scene database is being prepared (HTTP 202); retry after backfill completes.");
        this.name = "SceneBackfillingError";
    }
}

export function isSceneBackfillingError(error: unknown): error is SceneBackfillingError {
    return error instanceof SceneBackfillingError;
}

export type SceneReadBackend = "legacy" | "bim-query";

type SceneBackfillingListener = (backend: SceneReadBackend) => void;

const backfillingListeners = new Set<SceneBackfillingListener>();

/**
 * Subscribe to search-DB 202 (scene backfilling) signals. Every mapped 202 —
 * regardless of which endpoint produced it — notifies listeners so a single
 * app-level surface (banner + recovery poll) can react. Returns an unsubscribe.
 *
 * The scene being backfilled is always the one currently open, and its ids are
 * already known from the request context (`procoreContext` + `sceneId`), so the
 * signal identifies the read backend so recovery probes the same artifacts;
 * the 202 acceptance body is not read.
 */
export function onSceneBackfilling(listener: SceneBackfillingListener): () => void {
    backfillingListeners.add(listener);
    return () => {
        backfillingListeners.delete(listener);
    };
}

function notifySceneBackfilling(backend: SceneReadBackend): void {
    for (const listener of backfillingListeners) {
        listener(backend);
    }
}

/**
 * Wrap a `@procore/core-http` `parseResponse` callback so that a `202 Accepted`
 * (scene backfilling) is turned into a {@link SceneBackfillingError} before the
 * body is parsed as the expected payload. Non-2xx responses never reach here —
 * core-http throws `ResponseError` for those — so 404 handling is unaffected.
 */
export function throwWhenBackfilling<T>(
    parse: (response: Response) => T | Promise<T>,
    backend: SceneReadBackend = "legacy",
): (response: Response) => Promise<T> {
    return async (response: Response): Promise<T> => {
        if (response.status === 202) {
            notifySceneBackfilling(backend);
            throw new SceneBackfillingError();
        }
        return parse(response);
    };
}

/** BIM Query readiness depends on bim_index, independently of the legacy text index. */
export function throwWhenBimQueryBackfilling<T>(
    parse: (response: Response) => T | Promise<T>,
): (response: Response) => Promise<T> {
    return throwWhenBackfilling(parse, "bim-query");
}
